import { PDFDocument, rgb, degrees, StandardFonts } from "pdf-lib";
import FormatDate from "../../utils/formattedDate";
import QRCode from "qrcode";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

const A3_WIDTH = 842; // in points
const A3_HEIGHT = 1191; // in points

const getScaleFunction = (targetWidth, targetHeight, actualWidth, actualHeight) => {
  const actualShorter = Math.min(actualWidth, actualHeight);
  const actualLonger = Math.max(actualWidth, actualHeight);

  // Calculate the scaling factors based on the shorter and longer sides
  const widthScale = targetWidth / actualShorter;
  const heightScale = targetHeight / actualLonger;

  // Use the smaller scale to ensure content fits within both dimensions
  const scale = Math.min(widthScale, heightScale);
  return Math.max(Math.min(scale, 1), 0.6);
};

const MergeAndStampPDF = async (pdfFiles, QRCodeLink, Revision, Status, itemNumber, oidName, addStamp) => {
  let error;
  let font;
  let stampFont;

  try {
    console.log("Starting MergeAndStampPDF...");
    const mergedPDF = await PDFDocument.create();
    console.log("Created merged PDF document");

    const stampingOption = await axios.get(process.env.REACT_APP_BACKEND_URL + `/api/items/stampingOptions`, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    console.log("Retrieved stamping options:", stampingOption?.data);

    // Calculate the space needed for stamps
    const STAMP_AREA_HEIGHT = 100; // Space for QR code, stamps, and text
    console.log("Processing", pdfFiles.length, "PDF files");

    // STEP 1: Create expanded pages using embedPage()
    console.log("STEP 1: Creating expanded pages...");
    for (const pdfFile of pdfFiles) {
      console.log("Processing file:", pdfFile.name);
      const existingPDFBytes = await pdfFile.arrayBuffer();

      error = pdfFile.name;
      const pdfDoc = await PDFDocument.load(existingPDFBytes, {
        ignoreEncryption: true,
      });
      console.log("Loaded PDF document, pages:", pdfDoc.getPageCount());

      const pageCount = pdfDoc.getPageCount();

      for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
        console.log(`Creating expanded page ${pageIndex + 1}`);
        const originalPage = pdfDoc.getPages()[pageIndex];
        const { width, height } = originalPage.getSize();
        
        // Create a new page with extra height for stamp area at the bottom
        const expandedPage = mergedPDF.addPage([width, height + STAMP_AREA_HEIGHT]);
        
        // Embed the original page and draw it on the expanded page
        const embeddedPage = await mergedPDF.embedPage(originalPage);
        expandedPage.drawPage(embeddedPage, {
          x: 0,
          y: STAMP_AREA_HEIGHT, // Move content up to leave space at bottom
          width,
          height,
        });
        
        console.log(`Expanded page ${pageIndex + 1} created successfully`);
      }
    }

    // STEP 2: Save the expanded PDF (without stamps)
    console.log("STEP 2: Saving expanded PDF (before stamps)...");
    const expandedPDFBytes = await mergedPDF.save();
    console.log("Expanded PDF saved");

    // STEP 3: Load the expanded PDF and use copyPages() for stamping
    console.log("STEP 3: Loading expanded PDF for stamping...");
    const expandedPDFDoc = await PDFDocument.load(expandedPDFBytes);
    const stampablePDF = await PDFDocument.create();
    
    // Embed font in the stampable PDF
    font = await stampablePDF.embedFont(StandardFonts.Helvetica);
    console.log("Font embedded in stampable PDF");

    // Copy all expanded pages to the stampable PDF
    const expandedPageCount = expandedPDFDoc.getPageCount();
    const copiedPages = await stampablePDF.copyPages(expandedPDFDoc, Array.from({length: expandedPageCount}, (_, i) => i));
    
    copiedPages.forEach(page => {
      stampablePDF.addPage(page);
    });
    
    console.log(`Copied ${expandedPageCount} expanded pages for stamping`);

    // Save unstamped version (expanded but no stamps)
    const mergedUnstampedPDfBytes = expandedPDFBytes; // This is the expanded version without stamps

    const pages = stampablePDF.getPages();
    const totalPages = pages.length;
    let currentPage = 1;
    console.log("Total pages to process:", totalPages);

    console.log(`Status check - Status="${Status}", Status !== "markup": ${Status !== "markup"}`);
    
    if (Status !== "markup") {
      console.log("Status allows stamping - proceeding with stamp addition...");
      console.log("Adding stamps to pages...");
      
      for (const page of pages) {
        console.log(`Processing stamps for page ${currentPage}...`);
        const { width, height } = page.getSize();
        console.log(`Page dimensions: ${width}x${height}`);

        var opts = {
          margin: 0,
          color: {
            dark: Status === "draft" ? "#000000" : "#FF0000",
            light: "#FFFFFF",
          },
        };

        console.log("Generating QR code...");
        const qrCodeDataUrl = await QRCode.toDataURL(QRCodeLink, opts);
        console.log("QR code generated successfully");
        
        const qrImage = await stampablePDF.embedPng(qrCodeDataUrl);
        console.log("QR code embedded successfully");

        const qrOffset = 10;
        const scale = getScaleFunction(A3_WIDTH, A3_HEIGHT, width, height);
        console.log(`Scale factor: ${scale}`);

        const statusText = Status.toUpperCase();
        let waterMarkTextSize = 100 / scale;
        const cmToPoints = (cm) => cm * 28.3465;
        let qrSize = cmToPoints(2.5) / scale;
        let textSize = 8 / scale;

        console.log(`Calculated sizes - watermark: ${waterMarkTextSize}, qr: ${qrSize}, text: ${textSize}`);
        const stampText = Status === "draft" ? "NOT FOR CONSTRUCTION" : "APPROVED DRAWING FOR PRODUCTION";
        let stampSize = 8 / scale;

        const stampWidth = font.widthOfTextAtSize(stampText, stampSize);
        const stampHeight = stampSize;

        console.log(`Stamping Options: addWaterMarks=${stampingOption?.data?.addWaterMarks}, addQRCode=${stampingOption?.data?.addQRCode}, addStamp=${stampingOption?.data?.addStamp}`);

        // Calculate text width and height
        const textWidth = font.widthOfTextAtSize(statusText, waterMarkTextSize);
        const textHeight = waterMarkTextSize;

        // Calculate the center of the page
        const centerX = width / 2;
        const centerY = height / 2;

        // Calculate text positioning
        const textXCenter = textWidth / 2;
        const textYCenter = textHeight / 2;

        console.log(`Center coordinates: x:${centerX}, y:${centerY}`);

        if (stampingOption?.data?.addWaterMarks) {
          console.log("Adding watermark...");
          page.drawText(`${statusText}`, {
            x: centerX - textXCenter,
            y: 30,
            size: waterMarkTextSize,
            color: Status === "draft" ? rgb(0, 0, 0) : rgb(1, 0, 0),
            opacity: 0.1, // Reverted to original opacity
          });
          console.log(`Watermark added: "${statusText}" at x:${centerX - textXCenter}, y:30, size:${waterMarkTextSize}`);
        } else {
          console.log("Watermarks disabled in stamping options");
        }

        if (stampingOption?.data?.addQRCode) {
          console.log("Adding QR code...");
          page.drawImage(qrImage, {
            x: 30,
            y: 30,
            width: qrSize,
            height: qrSize,
            color: rgb(Status === "draft" ? 0 : 1, 0, 0),
            opacity: 0.5, // Reverted to original opacity
          });
          console.log(`QR code added at x:30, y:30, size:${qrSize}x${qrSize}`);
        } else {
          console.log("QR code disabled in stamping options");
        }

        if (stampingOption?.data?.addStamp) {
          console.log("Adding stamp rectangle and text...");
          page.drawRectangle({
            x: 30 + qrSize + 3,
            y: 30,
            width: qrSize * 2.5,
            height: qrSize / 2,
            color: rgb(1, 1, 1),
            borderColor: rgb(Status === "draft" ? 0 : 1, 0, 0),
            borderWidth: 2,
          });

          page.drawText(stampText, {
            x: 30 + qrSize + (qrSize * 2.5) / 2 - stampWidth / 2 + 3,
            y: 30 + qrSize / 4 + 1,
            size: stampSize,
            color: rgb(Status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawText(`${oidName.toUpperCase()} - ${itemNumber} Rev ${Revision}`, {
            x: 30 + qrSize + (qrSize * 2.5) / 2 - stampWidth / 2 + 3,
            y: 30 + qrSize / 4 - stampHeight - 1,
            size: stampSize,
            color: rgb(Status === "draft" ? 0 : 1, 0, 0),
          });
          console.log("Stamp rectangle and text added");
        } else {
          console.log("Stamp disabled in stamping options");
        }

        const textString = `${statusText} - ${itemNumber} Rev ${Revision} - Page ${currentPage} of ${totalPages} - ${FormatDate(new Date(), true)} - Scan QR code to confirm status.`;
        const headerFooterTextWidth = font.widthOfTextAtSize(textString, textSize);

        if (stampingOption?.data?.addWaterMarks) {
          // Header text at the very top of the page
          page.drawRectangle({
            x: 30 - 2,
            y: height - textSize - 10,
            width: headerFooterTextWidth + 4,
            height: textSize,
            color: rgb(1, 1, 1),
            borderColor: rgb(Status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawText(textString, {
            x: 30,
            y: height - textSize - 10,
            size: textSize,
            color: rgb(Status === "draft" ? 0 : 1, 0, 0),
          });

          // Footer text at the bottom of the page
          page.drawRectangle({
            x: 30 - 4,
            y: 30 - textSize - 3,
            width: headerFooterTextWidth + 4,
            height: textSize,
            color: rgb(1, 1, 1),
            borderColor: rgb(Status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawText(textString, {
            x: 30,
            y: 30 - textSize - 2,
            size: textSize,
            color: rgb(Status === "draft" ? 0 : 1, 0, 0),
          });
        }

        console.log(`Completed stamps for page ${currentPage}`);
        currentPage++;
      }
    } else {
      console.log("Status is 'markup' - SKIPPING ALL STAMPING");
    }

    console.log("Saving final stamped PDF (after adding all stamps)...");
    const modifiedPDFBytes = await stampablePDF.save();
    console.log("Final stamped PDF saved - this should contain all stamps");
    console.log("Creating blobs...");
    const blob = new Blob([modifiedPDFBytes], { type: "application/pdf" });
    const blob2 = new Blob([mergedUnstampedPDfBytes], {
      type: "application/pdf",
    });
    console.log("MergeAndStampPDF completed successfully");
    console.log("Returning stamped PDF blob (with stamps) and original blob (without stamps)");

    return { stampedPDF: blob, og: blob2 };
  } catch (err) {
    console.error("Error in MergeAndStampPDF:", err);
    console.error("Error stack:", err.stack);
    console.error("Error message:", err.message);
    console.error("Current file being processed:", error);
    throw new Error(`Error Applying Stamps: ${err.message}`);
  }
};

export const StampPDF = async (pdfBlob, file) => {
  return new Promise(async (resolve, reject) => {
    try {
      console.log("Starting StampPDF...");
      const status = file.status === "released" ? "released" : "draft";
      const revision = file.revision;
      const QRCodeLink = `${process.env.REACT_APP_WEBSITE}/status/files/${file.key}`;
      
      console.log("Loading original PDF...");
      const originalFile = await pdfBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(originalFile, {
        ignoreEncryption: false,
      });

      if (pdfDoc.isEncrypted) {
        throw new Error("The PDF is encrypted and cannot be processed.");
      }
      console.log("Original PDF loaded, pages:", pdfDoc.getPageCount());

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const pages = pdfDoc.getPages();
      const totalPages = pages.length;

      const opts = {
        margin: 0,
        color: {
          dark: status === "draft" ? "#000000" : "#FF0000",
          light: "#FFFFFF",
        },
      };

      const stampingOption = await axios.get(process.env.REACT_APP_BACKEND_URL + `/api/items/stampingOptions`, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      let currentPage = 1;
      for (const page of pages) {
        const qrCodeDataUrl = await QRCode.toDataURL(QRCodeLink, opts);
        const qrImage = await pdfDoc.embedPng(qrCodeDataUrl);
        const qrOffset = 10;
        const { width, height } = page.getSize();
        const scale = getScaleFunction(A3_WIDTH, A3_HEIGHT, width, height);

        const statusText = file.status.toUpperCase();
        let waterMarkTextSize = 100 / scale;
        const cmToPoints = (cm) => cm * 28.3465;
        let qrSize = cmToPoints(2.5) / scale;
        let textSize = 8 / scale;

        const stampText = status === "draft" ? "NOT FOR CONSTRUCTION" : "APPROVED DRAWING FOR PRODUCTION";
        let stampSize = 8 / scale;

        const stampWidth = font.widthOfTextAtSize(stampText, stampSize);
        const stampHeight = stampSize;
        const textWidth = font.widthOfTextAtSize(statusText, waterMarkTextSize);
        const textXCenter = textWidth / 2;

        // Calculate the center of the page
        const centerX = width / 2;
        const centerY = height / 2;

        console.log(`Stamping Options: addWaterMarks=${stampingOption?.data?.addWaterMarks}, addQRCode=${stampingOption?.data?.addQRCode}, addStamp=${stampingOption?.data?.addStamp}`);

        if (stampingOption?.data?.addWaterMarks) {
          page.drawText(`${statusText}`, {
            x: centerX - textXCenter,
            y: 30,
            size: waterMarkTextSize,
            color: status === "draft" ? rgb(0, 0, 0) : rgb(1, 0, 0),
            opacity: 0.1, // Reverted to original opacity
          });
        }

        if (stampingOption?.data?.addQRCode) {
          page.drawImage(qrImage, {
            x: 30,
            y: 30,
            width: qrSize,
            height: qrSize,
            color: rgb(status === "draft" ? 0 : 1, 0, 0),
            opacity: 0.5, // Reverted to original opacity
          });
        }

        if (stampingOption?.data?.addStamp) {
          page.drawRectangle({
            x: 30 + qrSize + 3,
            y: 30,
            width: qrSize * 2.5,
            height: qrSize / 2,
            color: rgb(1, 1, 1),
            borderColor: rgb(status === "draft" ? 0 : 1, 0, 0),
            borderWidth: 2,
          });

          page.drawText(stampText, {
            x: 30 + qrSize + (qrSize * 2.5) / 2 - stampWidth / 2 + 3,
            y: 30 + qrSize / 4 + 1,
            size: stampSize,
            color: rgb(status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawText(`${stampingOption?.data?.name.toUpperCase()}`, {
            x: 30 + qrSize + (qrSize * 2.5) / 2 - stampWidth / 2 + 3,
            y: 30 + qrSize / 4 - stampHeight - 1,
            size: stampSize,
            color: rgb(status === "draft" ? 0 : 1, 0, 0),
          });
        }

        const textString = `${statusText} - ${decodeURIComponent(file.fileName)}${file.fileExtension} Rev ${revision} - Page ${currentPage} of ${totalPages} - ${FormatDate(new Date(), true)} - Scan QR code to confirm status.`;
        const headerFooterTextWidth = font.widthOfTextAtSize(textString, textSize);

        if (stampingOption?.data?.addWaterMarks) {
          page.drawRectangle({
            x: 30 - 2,
            y: height - textSize - 10,
            width: headerFooterTextWidth + 4,
            height: textSize,
            color: rgb(1, 1, 1),
            borderColor: rgb(status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawText(textString, {
            x: 30,
            y: height - textSize - 10,
            size: textSize,
            color: rgb(status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawRectangle({
            x: 30 - 4,
            y: 30 - textSize - 3,
            width: headerFooterTextWidth + 4,
            height: textSize,
            color: rgb(1, 1, 1),
            borderColor: rgb(status === "draft" ? 0 : 1, 0, 0),
          });

          page.drawText(textString, {
            x: 30,
            y: 30 - textSize - 2,
            size: textSize,
            color: rgb(status === "draft" ? 0 : 1, 0, 0),
          });
        }

        currentPage++;
      }

      const stampedPDF = await pdfDoc.save();
      const blob = new Blob([stampedPDF], { type: "application/pdf" });
      resolve(blob);
    } catch (error) {
      console.error("Error processing PDF:", error);
      console.error("Error stack:", error.stack);
      console.error("Error message:", error.message);
      reject(new Error(`Failed to process and stamp the PDF: ${error.message}`));
    }
  });
};

export default MergeAndStampPDF;